import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, TrendingUp, Award, Search, Mail, Download, MessageCircle, Calendar, Clock, ArrowRight, Zap, FileBarChart, Send, HelpCircle, CheckCircle, AlertCircle, Activity, Filter, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
export function AdminHub() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedSkill, setSelectedSkill] = useState('');
  const [reminderLearner, setReminderLearner] = useState('');
  const [isReminderDropdownOpen, setIsReminderDropdownOpen] = useState(false);
  const [isSkillDropdownOpen, setIsSkillDropdownOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const reminderDropdownRef = useRef<HTMLDivElement>(null);
  const skillDropdownRef = useRef<HTMLDivElement>(null);
  const incompleteLearners = [{
    value: 'rachel-patel',
    name: 'Rachel Patel',
    email: 'rachel.patel@example.com'
  }, {
    value: 'david-nguyen',
    name: 'David Nguyen',
    email: 'd.nguyen@example.com'
  }, {
    value: 'karen-brooks',
    name: 'Karen Brooks',
    email: 'k.brooks@example.com'
  }, {
    value: 'marcus-hall',
    name: 'Marcus Hall',
    email: 'marcus.h@example.com'
  }, {
    value: 'tanya-foster',
    name: 'Tanya Foster',
    email: 'tanya.f@example.com'
  }];
  const skillOptions = [{
    value: 'html',
    label: 'HTML Basics'
  }, {
    value: 'css',
    label: 'CSS Fundamentals'
  }, {
    value: 'js',
    label: 'JavaScript Basics'
  }, {
    value: 'responsive',
    label: 'Responsive Design'
  }];
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (reminderDropdownRef.current && !reminderDropdownRef.current.contains(event.target as Node)) {
        setIsReminderDropdownOpen(false);
      }
      if (skillDropdownRef.current && !skillDropdownRef.current.contains(event.target as Node)) {
        setIsSkillDropdownOpen(false);
      }
    };
    if (isUserMenuOpen || isReminderDropdownOpen || isSkillDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, isReminderDropdownOpen, isSkillDropdownOpen]);
  // Mock data
  const quickStats = [{
    label: 'Active Learners',
    value: '5',
    change: '+12%',
    trend: 'up'
  }, {
    label: 'Pending Invites',
    value: '5',
    change: '-5%',
    trend: 'down'
  }, {
    label: 'Avg. Score',
    value: '87%',
    change: '+3%',
    trend: 'up'
  }, {
    label: 'Support Tickets',
    value: '4',
    change: '0%',
    trend: 'neutral'
  }];
  const recentActivity = [{
    type: 'user',
    message: 'Sarah Johnson completed "Advanced JavaScript"',
    time: '5 min ago',
    icon: CheckCircle,
    color: 'text-green-600'
  }, {
    type: 'alert',
    message: '3 learners need attention in "CSS Fundamentals"',
    time: '12 min ago',
    icon: AlertCircle,
    color: 'text-orange-600'
  }, {
    type: 'report',
    message: 'Monthly Analytics Report generated',
    time: '1 hour ago',
    icon: FileBarChart,
    color: 'text-blue-600'
  }, {
    type: 'user',
    message: 'New learner registered: Michael Chen',
    time: '2 hours ago',
    icon: Users,
    color: 'text-[#30718D]'
  }];
  const navigationCards = [{
    title: 'Analytics Dashboard',
    description: 'View detailed performance metrics and insights',
    icon: BarChart3,
    color: 'from-blue-500 to-blue-600',
    stats: '248 learners tracked',
    link: '/analytics'
  }, {
    title: 'User Management',
    description: 'Manage learner accounts and permissions',
    icon: UserCog,
    color: 'from-[#30718D] to-[#265a70]',
    stats: '18 pending invites',
    link: '/assessment'
  }, {
    title: 'Reports',
    description: 'Generate and download custom reports',
    icon: FileText,
    color: 'from-green-500 to-green-600',
    stats: 'Last report: 1 hour ago',
    link: '/feedback'
  }];
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-1123">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-1124">
        <div className="max-w-[1440px] mx-auto px-8" data-id="element-1125">
          <div className="flex items-center h-16" data-id="element-1126">
            {/* Logo */}
            <div className="text-[#E53E3E] font-bold text-xl" data-id="element-1127">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-8 ml-12" data-id="element-1128">
              <Link to="/admin" className="flex items-center gap-2 text-[#00638C] border-b-2 border-[#00638C] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-1129">
                <Home size={18} data-id="element-1130" />
                <span data-id="element-1131">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1132">
                <BarChart3 size={18} data-id="element-1133" />
                <span data-id="element-1134">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1135">
                <FileText size={18} data-id="element-1136" />
                <span data-id="element-1137">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1138">
                <UserCog size={18} data-id="element-1139" />
                <span data-id="element-1140">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-4 ml-auto" data-id="element-1141">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-1142">
                <Bell size={20} data-id="element-1143" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-1144"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-1145">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-1146">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-1147">
                    LT
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-1148">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-1149" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-1150">
                    <div className="py-2" data-id="element-1151">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1152">
                        <Users size={18} className="text-gray-600" data-id="element-1153" />
                        <span className="font-medium text-gray-900" data-id="element-1154">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1155">
                        <Home size={18} className="text-gray-600" data-id="element-1156" />
                        <span className="font-medium text-gray-900" data-id="element-1157">
                          Learner Hub
                        </span>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1158">
                        <UserCog size={18} className="text-gray-600" data-id="element-1159" />
                        <span className="font-medium text-gray-900" data-id="element-1160">
                          Admin Hub
                        </span>
                      </button>
                      <Link to="/settings" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1161">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1162">
                          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" data-id="element-1163"></path>
                          <circle cx="12" cy="12" r="3" data-id="element-1164"></circle>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1165">
                          Settings
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-1166"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1167">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1168">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-1169"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-1170"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-1171"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1172">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-8 py-8" data-id="element-1173">
        {/* Header */}
        <div className="mb-8" data-id="element-1174">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1175">
            Welcome back, Linda
          </h1>
          <p className="text-gray-600" data-id="element-1176">
            Here's what's happening with your learning platform today
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-6 mb-8" data-id="element-1177">
          {quickStats.map((stat, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-1178">
              <div className="flex items-center justify-between mb-2" data-id="element-1179">
                <span className="text-sm font-medium text-gray-600" data-id="element-1180">
                  {stat.label}
                </span>
                {stat.trend === 'up' ? <TrendingUp size={20} className="text-green-600" data-id="element-1181" /> : stat.trend === 'down' ? <TrendingUp size={20} className="text-red-600 rotate-180" data-id="element-1182" /> : <Activity size={20} className="text-gray-400" data-id="element-1183" />}
              </div>
              <div className="flex items-end justify-between" data-id="element-1184">
                <div className="text-3xl font-bold text-gray-900" data-id="element-1185">
                  {stat.value}
                </div>
                <span className={`text-lg font-bold ${stat.trend === 'up' ? 'text-green-600' : stat.trend === 'down' ? 'text-red-600' : 'text-gray-500'}`} data-id="element-1186">
                  {stat.change}
                </span>
              </div>
            </motion.div>)}
        </div>

        {/* Quick Actions Section */}
        <div className="grid grid-cols-2 gap-6 mb-8" data-id="element-1187">
          {/* Quick Search */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-1188">
            <div className="flex items-center gap-3 mb-4" data-id="element-1189">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center" data-id="element-1190">
                <Search size={20} className="text-blue-600" data-id="element-1191" />
              </div>
              <div data-id="element-1192">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1193">
                  Quick Search
                </h3>
                <p className="text-sm text-gray-600" data-id="element-1194">
                  Find learners or skills instantly
                </p>
              </div>
            </div>
            <div className="relative" data-id="element-1195">
              <input type="text" placeholder="Search for learner name, email, or skill..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00638C] focus:border-transparent text-sm" data-id="element-1196" />
              <button className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors" data-id="element-1197">
                <Search size={16} data-id="element-1198" />
              </button>
            </div>
          </motion.div>

          {/* Send Reminder */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3
        }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-1199">
            <div className="flex items-center gap-3 mb-4" data-id="element-1200">
              <div className="w-10 h-10 rounded-lg bg-[#30718D]/10 flex items-center justify-center" data-id="element-1201">
                <Mail size={20} className="text-[#30718D]" data-id="element-1202" />
              </div>
              <div data-id="element-1203">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1204">
                  Send Account Creation Email
                </h3>
                <p className="text-sm text-gray-600" data-id="element-1205">
                  Send learners reminder emails to complete account creation
                </p>
              </div>
            </div>
            <div className="flex gap-2" data-id="element-1206">
              <div className="relative flex-1" ref={reminderDropdownRef} data-id="element-1207">
                <button onClick={() => {
                setIsReminderDropdownOpen(!isReminderDropdownOpen);
                setIsSkillDropdownOpen(false);
              }} className="flex items-center gap-2 w-full px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white text-sm font-medium" data-id="element-1208">
                  <Users size={18} className="text-[#30718D]" data-id="element-1209" />
                  <span className="flex-1 text-left text-gray-700" data-id="element-1210">
                    {reminderLearner ? incompleteLearners.find(l => l.value === reminderLearner)?.name : 'All incomplete learners (5)'}
                  </span>
                  <ChevronDown size={16} className={`text-gray-400 transition-transform ${isReminderDropdownOpen ? 'rotate-180' : ''}`} data-id="element-1211" />
                </button>

                {isReminderDropdownOpen && <div className="absolute top-full mt-2 left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-80 overflow-y-auto" data-id="element-1212">
                    <button onClick={() => {
                  setReminderLearner('');
                  setIsReminderDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 text-sm ${reminderLearner === '' ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-1213">
                      All incomplete learners
                    </button>
                    {incompleteLearners.map(learner => <button key={learner.value} onClick={() => {
                  setReminderLearner(learner.value);
                  setIsReminderDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${reminderLearner === learner.value ? 'bg-[#30718D]/5' : ''}`} data-id="element-1214">
                        <div className={`font-medium text-sm ${reminderLearner === learner.value ? 'text-[#30718D]' : 'text-gray-900'}`} data-id="element-1215">
                          {learner.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1216">
                          {learner.email}
                        </div>
                      </button>)}
                  </div>}
              </div>
              <button className="px-4 py-2.5 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors flex items-center gap-2 text-sm font-medium" data-id="element-1217">
                <Send size={16} data-id="element-1218" />
                Send
              </button>
            </div>
          </motion.div>
        </div>

        {/* Admin Insights & Last Report */}
        <div className="grid grid-cols-2 gap-6 mb-8" data-id="element-1219">
          {/* Admin Insights */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-1220">
            <div className="flex items-center gap-3 mb-4" data-id="element-1221">
              <div className="w-10 h-10 rounded-lg bg-[#00638C]/10 flex items-center justify-center" data-id="element-1222">
                <FileBarChart size={20} className="text-[#00638C]" data-id="element-1223" />
              </div>
              <div data-id="element-1224">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1225">
                  Admin Insights
                </h3>
                <p className="text-sm text-gray-600" data-id="element-1226">
                  Generate custom skills reports
                </p>
              </div>
            </div>
            <div className="space-y-3" data-id="element-1227">
              <div className="relative" ref={skillDropdownRef} data-id="element-1228">
                <button onClick={() => {
                setIsSkillDropdownOpen(!isSkillDropdownOpen);
                setIsReminderDropdownOpen(false);
              }} className="flex items-center gap-2 w-full px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white text-sm font-medium" data-id="element-1229">
                  <span className="flex-1 text-left text-gray-700" data-id="element-1230">
                    {selectedSkill ? skillOptions.find(s => s.value === selectedSkill)?.label : 'Select a skill...'}
                  </span>
                  <ChevronDown size={16} className={`text-gray-400 transition-transform ${isSkillDropdownOpen ? 'rotate-180' : ''}`} data-id="element-1231" />
                </button>

                {isSkillDropdownOpen && <div className="absolute top-full mt-2 left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10" data-id="element-1232">
                    <button onClick={() => {
                  setSelectedSkill('');
                  setIsSkillDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 text-sm ${selectedSkill === '' ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-1233">
                      Select a skill...
                    </button>
                    {skillOptions.map(skill => <button key={skill.value} onClick={() => {
                  setSelectedSkill(skill.value);
                  setIsSkillDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 text-sm ${selectedSkill === skill.value ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-1234">
                        {skill.label}
                      </button>)}
                  </div>}
              </div>
              <button className="w-full px-4 py-3 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors flex items-center justify-center gap-2 text-sm font-semibold shadow-sm" data-id="element-1235">
                <Download size={16} data-id="element-1236" />
                Download Report
              </button>
            </div>
          </motion.div>

          {/* Last Generated Report */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-1237">
            <div className="flex items-center justify-between mb-4" data-id="element-1238">
              <div className="flex items-center gap-3" data-id="element-1239">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center" data-id="element-1240">
                  <FileText size={20} className="text-green-600" data-id="element-1241" />
                </div>
                <div data-id="element-1242">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-1243">
                    Last Generated Report
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-1244">
                    Monthly Analytics - December 2024
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-3" data-id="element-1245">
              <div className="flex items-center justify-between py-2 border-b border-gray-100" data-id="element-1246">
                <span className="text-sm text-gray-600" data-id="element-1247">Generated</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-1248">
                  1 hour ago
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100" data-id="element-1249">
                <span className="text-sm text-gray-600" data-id="element-1250">File Size</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-1251">
                  2.4 MB
                </span>
              </div>
              <div className="flex items-center justify-between py-2" data-id="element-1252">
                <span className="text-sm text-gray-600" data-id="element-1253">Format</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-1254">PDF</span>
              </div>
              <button className="w-full px-4 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-sm font-medium" data-id="element-1255">
                <Download size={16} data-id="element-1256" />
                Download Again
              </button>
            </div>
          </motion.div>
        </div>

        {/* Navigation Cards & Recent Activity */}
        <div className="grid grid-cols-3 gap-6 mb-8" data-id="element-1257">
          {navigationCards.map((card, index) => {
          const Icon = card.icon;
          return <Link key={index} to={card.link} data-id="element-1258">
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.6 + index * 0.1
            }} className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-1259">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`} data-id="element-1260">
                    <Icon size={24} className="text-white" data-id="element-1261" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#00638C] transition-colors" data-id="element-1262">
                    {card.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4" data-id="element-1263">
                    {card.description}
                  </p>
                  <div className="flex items-center justify-between" data-id="element-1264">
                    <span className="text-xs font-medium text-gray-500" data-id="element-1265">
                      {card.stats}
                    </span>
                    <ArrowRight size={16} className="text-gray-400 group-hover:text-[#00638C] group-hover:translate-x-1 transition-all" data-id="element-1266" />
                  </div>
                </motion.div>
              </Link>;
        })}
        </div>

        {/* Recent Activity & Support */}
        <div className="grid grid-cols-3 gap-6" data-id="element-1267">
          {/* Recent Activity - Takes 2 columns */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.9
        }} className="col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-1268">
            <div className="flex items-center justify-between mb-6" data-id="element-1269">
              <div className="flex items-center gap-3" data-id="element-1270">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center" data-id="element-1271">
                  <Activity size={20} className="text-orange-600" data-id="element-1272" />
                </div>
                <div data-id="element-1273">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-1274">
                    Recent Activity
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-1275">
                    Latest updates from your platform
                  </p>
                </div>
              </div>
              <button className="text-sm font-medium text-[#00638C] hover:text-[#004d6d] transition-colors flex items-center gap-1" data-id="element-1276">
                View All
                <ExternalLink size={14} data-id="element-1277" />
              </button>
            </div>
            <div className="space-y-4" data-id="element-1278">
              {recentActivity.map((activity, index) => {
              const Icon = activity.icon;
              return <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 1 + index * 0.1
              }} className="flex items-start gap-4 p-4 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1279">
                    <div className={`w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 ${activity.color}`} data-id="element-1280">
                      <Icon size={18} data-id="element-1281" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-1282">
                      <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-1283">
                        {activity.message}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-1284">
                        <Clock size={12} data-id="element-1285" />
                        <span data-id="element-1286">{activity.time}</span>
                      </div>
                    </div>
                  </motion.div>;
            })}
            </div>
          </motion.div>

          {/* Contact Support */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1
        }} className="bg-gradient-to-br from-[#30718D] to-[#265a70] rounded-xl p-6 shadow-lg text-white" data-id="element-1287">
            <div className="flex items-center gap-3 mb-4" data-id="element-1288">
              <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center" data-id="element-1289">
                <HelpCircle size={20} className="text-white" data-id="element-1290" />
              </div>
              <div data-id="element-1291">
                <h3 className="text-lg font-bold" data-id="element-1292">Need Help?</h3>
                <p className="text-sm text-blue-100" data-id="element-1293">
                  Contact our support team
                </p>
              </div>
            </div>
            <p className="text-sm text-blue-100 mb-6" data-id="element-1294">
              Our support team is available 24/7 to help you with any questions
              or issues you may have.
            </p>
            <div className="space-y-3" data-id="element-1295">
              <button className="w-full px-4 py-3 bg-white text-[#30718D] rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-sm font-semibold shadow-sm" data-id="element-1296">
                <MessageCircle size={16} data-id="element-1297" />
                Start Live Chat
              </button>
              <button className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors flex items-center justify-center gap-2 text-sm font-medium" data-id="element-1298">
                <Mail size={16} data-id="element-1299" />
                Email Support
              </button>
            </div>
            <div className="mt-6 pt-6 border-t border-white/20" data-id="element-1300">
              <div className="flex items-center justify-between text-sm" data-id="element-1301">
                <span className="text-blue-100" data-id="element-1302">Avg. Response Time</span>
                <span className="font-semibold" data-id="element-1303">~2 minutes</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}